<template>
  <v-row v-if="isMounted && isFetching">
    <v-col align="center">
      <v-progress-circular
        :size="80"
        color="red-darken-2"
        indeterminate
      ></v-progress-circular>
    </v-col>
  </v-row>

  <template v-if="shownItems && shownItems.length">
    <template v-for="item in shownItems" :key="item.id">
      <v-row dense>
        <v-col>
          <OccasionCard
            :hide-details="hideDetails"
            :hide-info-button="hideInfoButton"
            :hide-share-button="hideShareButton"
            :register-as-link="registerAsLink"
            :show-embed-code="showEmbedCode"
            :value="item"
          />
        </v-col>
      </v-row>
    </template>
  </template>

  <v-row v-if="showAllButton && hasMore">
    <v-col>
      <v-btn color="primary" block size="large" @click="isAllShown = true"
        >Show All</v-btn
      ></v-col
    >
  </v-row>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMounted } from '@vueuse/core'
import {
  FILTER_DELIVERY_METHOD_ALL,
  FILTER_METHODOLOGY_ALL,
  FILTER_OCCASION_TYPE_ALL,
  FILTER_WHAT_ALL,
  FILTER_WHEN_HALF_DAY,
  FILTER_WHEN_UPCOMING,
  FILTER_WHEN_WEEKEND,
  FILTER_WHO_ALL
} from '#root/lib/consts'
import type { OccasionFeedItem } from '#root/types/occasions'
import { useOccasionsStoreOrThrow } from '#root/lib/useOccasionsStore'

type OccasionFiltersValue = {
  deliveryMethod?: string
  methodology?: string
  occasionType?: string
  productCodes?: string[]
  what?: string
  when?: string
  who?: string
}

const props = withDefaults(
  defineProps<{
    all?: boolean
    filtersValue?: OccasionFiltersValue
    hideDetails?: boolean
    hideInfoButton?: boolean
    hideShareButton?: boolean
    limit?: number
    registerAsLink?: boolean
    showAllButton?: boolean
    showEmbedCode?: boolean
  }>(),
  {
    all: false,
    filtersValue: undefined,
    hideDetails: false,
    hideInfoButton: false,
    hideShareButton: false,
    limit: 10000,
    registerAsLink: false,
    showAllButton: false,
    showEmbedCode: false
  }
)

const isAllShown = ref(false)
const isMounted = useMounted()
const { indexes, isFetching, items, upcomingIndicies, yearMonthIndex } =
  useOccasionsStoreOrThrow()
const filteredItems = computed(() => {
  const filters = props.filtersValue || {}

  let indicies = upcomingIndicies.value

  if (!(indexes.value && items.value && indicies)) return []

  if (
    typeof filters.deliveryMethod === 'string' &&
    filters.deliveryMethod !== FILTER_DELIVERY_METHOD_ALL
  ) {
    const ind =
      (indexes.value.delivery_method_code &&
        indexes.value.delivery_method_code[
          filters.deliveryMethod.toUpperCase()
        ]) ||
      []
    indicies = indicies.filter(i => ind.includes(i))
  }

  if (
    typeof filters.methodology === 'string' &&
    filters.methodology !== FILTER_METHODOLOGY_ALL
  ) {
    const ind =
      (indexes.value.methodology_code &&
        indexes.value.methodology_code[filters.methodology.toUpperCase()]) ||
      []
    indicies = indicies.filter(i => ind.includes(i))
  }

  if (
    typeof filters.occasionType === 'string' &&
    filters.occasionType !== FILTER_OCCASION_TYPE_ALL
  ) {
    const ind =
      (indexes.value.occasion_type_code &&
        indexes.value.occasion_type_code[filters.occasionType.toUpperCase()]) ||
      []
    indicies = indicies.filter(i => ind.includes(i))
  }

  if (Array.isArray(filters.productCodes) && filters.productCodes.length) {
    let ind: number[] = []
    filters.productCodes.forEach(value => {
      const productCodeIndex =
        indexes.value && indexes.value.product_code
          ? indexes.value.product_code[value.toUpperCase()] || []
          : []
      ind = ind.concat(productCodeIndex)
    })
    indicies = indicies.filter(i => ind.includes(i))
  }

  if (typeof filters.what === 'string' && filters.what !== FILTER_WHAT_ALL) {
    let ind: number[] = []
    filters.what.split(',').forEach(value => {
      const productCodeIndex =
        indexes.value && indexes.value.product_code
          ? indexes.value.product_code[value.toUpperCase()] || []
          : []
      ind = ind.concat(productCodeIndex)
    })
    // const ind =
    //   (indexes.value.product_code &&
    //     indexes.value.product_code[filters.what.toUpperCase()]) ||
    //   []
    indicies = indicies.filter(i => ind.includes(i))
  }

  if (typeof filters.when === 'string') {
    if (filters.when === FILTER_WHEN_HALF_DAY) {
      const ind =
        indexes.value && indexes.value.is_half_day
          ? indexes.value.is_half_day.TRUE || []
          : []
      indicies = indicies.filter(i => ind.includes(i))
    } else if (filters.when === FILTER_WHEN_WEEKEND) {
      const ind =
        indexes.value && indexes.value.is_weekend
          ? indexes.value.is_weekend.TRUE || []
          : []
      indicies = indicies.filter(i => ind.includes(i))
    } else if (filters.when !== FILTER_WHEN_UPCOMING) {
      const ind = yearMonthIndex.value[filters.when] || []
      indicies = indicies.filter(i => ind.includes(i))
    }
  }

  if (typeof filters.who === 'string' && filters.who !== FILTER_WHO_ALL) {
    const ind =
      indexes.value && indexes.value.person_code
        ? indexes.value.person_code[filters.who.toUpperCase()] || []
        : []
    indicies = indicies.filter(i => ind.includes(i))
  }

  const result: OccasionFeedItem[] = []
  for (const i of indicies) {
    const item = items.value![i]
    if (item != null) result.push(item)
  }
  return result
})
const shownItems = computed(() => {
  return props.all || isAllShown.value
    ? filteredItems.value
    : filteredItems.value.slice(0, props.limit)
})
const hasMore = computed(() => {
  return (
    filteredItems.value &&
    shownItems.value &&
    filteredItems.value.length > shownItems.value.length
  )
})

defineExpose({
  shownItems
})
</script>
